// Core calculation logic for PendelPilot - all client-side, no PII leaves the browser

export const NATIONAL_BENCHMARK = {
  avg_distance_km: 17.0,
  avg_minutes_one_way: 30.0,
  avg_annual_hours: 234.0,
  avg_annual_cost_eur: 2800.0,
};

export function benchmarkComparison(results) {
  const pct = (v, avg) => Math.round(((v - avg) / avg) * 100);
  return {
    distance_pct: pct(results.distance_km, NATIONAL_BENCHMARK.avg_distance_km),
    time_pct: pct(results.minutes_one_way, NATIONAL_BENCHMARK.avg_minutes_one_way),
    cost_pct: pct(results.annual_cost, NATIONAL_BENCHMARK.avg_annual_cost_eur),
    hours_pct: pct(results.annual_hours, NATIONAL_BENCHMARK.avg_annual_hours),
  };
}

export const FUEL_DEFAULTS = {
  e5: 1.79,
  e10: 1.73,
  diesel: 1.69,
};

export const PRESETS = {
  kurz: {
    label: 'Kurzstrecke',
    distance_km: 10,
    office_days: 5,
    consumption: 7.5,
    fuel_type: 'e10',
    minutes_one_way: 20,
  },
  mittel: {
    label: 'Mittelstrecke',
    distance_km: 25,
    office_days: 4,
    consumption: 6.5,
    fuel_type: 'diesel',
    minutes_one_way: 35,
  },
  lang: {
    label: 'Langstrecke',
    distance_km: 50,
    office_days: 3,
    consumption: 6.0,
    fuel_type: 'diesel',
    minutes_one_way: 55,
  },
};

// Work weeks per year ~ 46 (after vacation & holidays)
const WORK_WEEKS = 46;

export function computeResults(input) {
  const distance_km = Number(input.distance_km) || 0;
  const office_days = Math.min(7, Math.max(0, Number(input.office_days) || 0));
  const fuel_price = Number(input.fuel_price) || FUEL_DEFAULTS[input.fuel_type] || 1.75;
  const consumption = Number(input.consumption) || 7.0;
  const minutes_one_way = Math.max(1, Number(input.minutes_one_way) || estimateMinutes(distance_km));

  const trips_per_week = office_days;
  const trips_per_year = trips_per_week * WORK_WEEKS;
  const daily_km = distance_km * 2;
  const annual_km = daily_km * trips_per_year;
  const monthly_km = annual_km / 12;

  const liters_per_year = (annual_km / 100) * consumption;
  const fuel_cost_year = liters_per_year * fuel_price;
  const wear_per_km = 0.12;
  const wear_cost_year = annual_km * wear_per_km;
  const annual_cost = fuel_cost_year + wear_cost_year;
  const monthly_cost = annual_cost / 12;

  const minutes_year = minutes_one_way * 2 * trips_per_year;
  const annual_hours = minutes_year / 60;
  const annual_days = annual_hours / 24;
  const annual_work_weeks = annual_hours / 40;

  const pp_low = Math.min(20, distance_km) * 0.30;
  const pp_high = Math.max(0, distance_km - 20) * 0.38;
  const pauschale_per_day = pp_low + pp_high;
  const pauschale_year = pauschale_per_day * trips_per_year;

  // CO2: Diesel ~2.65 kg/L, E5/E10 ~2.32 kg/L (Umweltbundesamt 2024)
  const co2_per_liter = input.fuel_type === 'diesel' ? 2.65 : 2.32;
  const annual_co2_kg = liters_per_year * co2_per_liter;
  // 1 mature tree absorbs ~22 kg CO2 per year (BUND)
  const trees_needed = annual_co2_kg / 22;
  // Equiv. flights MUC-PMI return ~ 530 kg per pax
  const flights_equivalent = annual_co2_kg / 530;

  const score = computeScore({ distance_km, minutes_one_way, annual_cost, office_days });
  const { score_label, score_color, score_explanation } = labelForScore(score);
  const recommendations = buildRecommendations({
    distance_km,
    office_days,
    annual_cost,
    annual_hours,
    fuel_type: input.fuel_type,
    consumption,
    minutes_one_way,
  });
  const comparisons = buildComparisons({ annual_cost, annual_hours, annual_work_weeks });

  return {
    distance_km,
    office_days,
    fuel_price,
    consumption,
    minutes_one_way,
    daily_km,
    annual_km,
    monthly_km,
    liters_per_year,
    fuel_cost_year,
    wear_cost_year,
    annual_cost,
    monthly_cost,
    annual_hours,
    annual_days,
    annual_work_weeks,
    pauschale_year,
    annual_co2_kg,
    trees_needed,
    flights_equivalent,
    score,
    score_label,
    score_color,
    score_explanation,
    recommendations,
    comparisons,
  };
}

function estimateMinutes(km) {
  // Rough urban-mixed average 50 km/h
  return Math.round((km / 50) * 60);
}

function computeScore({ distance_km, minutes_one_way, annual_cost, office_days }) {
  // Component scores: shorter/cheaper/less days = higher
  // Distance: 0–80km mapped
  const distScore = clamp(100 - (distance_km / 80) * 100);
  // Time: 0–90 min mapped
  const timeScore = clamp(100 - (minutes_one_way / 90) * 100);
  // Cost: 0–8000 €/yr mapped
  const costScore = clamp(100 - (annual_cost / 8000) * 100);
  // Office days: 0 days = 100, 5 days = lower
  const officeScore = clamp(100 - (office_days / 5) * 60);

  const score = Math.round(
    distScore * 0.25 + timeScore * 0.30 + costScore * 0.30 + officeScore * 0.15
  );
  return Math.max(0, Math.min(100, score));
}

function clamp(v) {
  return Math.max(0, Math.min(100, v));
}

function labelForScore(score) {
  if (score >= 70) {
    return {
      score_label: 'Sehr guter Arbeitsweg',
      score_color: '#2A9D8F',
      score_explanation:
        'Dein Arbeitsweg ist im grünen Bereich. Strecke, Zeit und Kosten sind verträglich und belasten weder Geldbeutel noch Lebenszeit übermäßig.',
    };
  }
  if (score >= 40) {
    return {
      score_label: 'Durchschnittlicher Arbeitsweg',
      score_color: '#F4A261',
      score_explanation:
        'Dein Arbeitsweg liegt im Mittelfeld. Es gibt Potenzial zu sparen — vor allem über Homeoffice-Tage, Fahrgemeinschaften oder Bahn.',
    };
  }
  return {
    score_label: 'Hohe Pendelbelastung',
    score_color: '#E63946',
    score_explanation:
      'Dein Arbeitsweg kostet dich viel Geld und Lebenszeit. Es lohnt sich, ernsthaft über Alternativen nachzudenken: mehr Homeoffice, Bahn, oder mittelfristig ein Umzug.',
  };
}

function buildRecommendations(p) {
  const recs = [];
  const { distance_km, office_days, annual_cost, annual_hours, fuel_type, consumption, minutes_one_way } = p;

  if (office_days >= 4) {
    const homeofficeSavings = (annual_cost / office_days) * 1;
    recs.push({
      icon: 'house',
      title: 'Homeoffice prüfen',
      detail: `Schon 1 Tag Homeoffice/Woche spart dir ca. ${formatEUR(homeofficeSavings)} pro Jahr und ${Math.round(annual_hours / office_days)} h Lebenszeit.`,
      saving_eur: homeofficeSavings,
      cta_label: 'Mit Chef:in besprechen',
    });
  }

  if (distance_km >= 15) {
    const ride_saving = annual_cost * 0.45;
    recs.push({
      icon: 'users-three',
      title: 'Fahrgemeinschaft bilden',
      detail: `Mit 1 Mitfahrer:in halbierst du fast deine Pendelkosten — Ersparnis ca. ${formatEUR(ride_saving)}/Jahr.`,
      saving_eur: ride_saving,
      cta_label: 'BlaBlaCar / goFLUX prüfen',
      affiliate_url: 'https://www.blablacar.de/',
    });
  }

  if (distance_km >= 20 && minutes_one_way >= 30) {
    const ticket_year = 58 * 12;
    const bahn_saving = Math.max(0, annual_cost - ticket_year);
    if (bahn_saving > 200) {
      recs.push({
        icon: 'train',
        title: 'Deutschlandticket / Bahn prüfen',
        detail: `Mit dem Deutschlandticket (58 €/Mon.) sparst du potenziell ${formatEUR(bahn_saving)}/Jahr — sofern eine sinnvolle Verbindung existiert.`,
        saving_eur: bahn_saving,
        cta_label: 'Verbindung & Ticket prüfen',
        affiliate_url: 'https://www.bahn.de/angebot/abo/deutschlandticket',
      });
    }
  }

  if (annual_cost >= 4000 && distance_km >= 30) {
    recs.push({
      icon: 'house-line',
      title: 'Umzug langfristig prüfen',
      detail: `Du gibst ${formatEUR(annual_cost)}/Jahr fürs Pendeln aus. In 5 Jahren sind das ${formatEUR(annual_cost * 5)} — oft mehr als ein Umzug kostet.`,
      saving_eur: annual_cost * 0.6,
      cta_label: 'Wohnung in Job-Nähe suchen',
      affiliate_url: 'https://www.immoscout24.de/wohnen/',
    });
  }

  const annual_km = distance_km * 2 * office_days * WORK_WEEKS;
  if (annual_km >= 12000 && consumption >= 5.5) {
    const ev_saving_per_km = fuel_type === 'diesel' ? 0.05 : 0.07;
    const ev_saving = annual_km * ev_saving_per_km;
    recs.push({
      icon: 'lightning',
      title: 'E-Auto könnte sich lohnen',
      detail: `Bei ${Math.round(annual_km).toLocaleString('de-DE')} km/Jahr sparst du ca. ${formatEUR(ev_saving)} an Energiekosten — plus geringere Wartung.`,
      saving_eur: ev_saving,
      cta_label: 'E-Auto-Angebote ansehen',
      affiliate_url: 'https://www.heycar.de/elektroauto',
    });
  }

  if (recs.length === 0) {
    recs.push({
      icon: 'check-circle',
      title: 'Du bist gut aufgestellt',
      detail: 'Dein Pendel-Profil ist effizient. Behalte Spritpreise im Auge und nutze die Pendlerpauschale bei der Steuer.',
      saving_eur: 0,
    });
  }

  return recs.slice(0, 4);
}

function buildComparisons({ annual_cost, annual_hours, annual_work_weeks }) {
  const out = [];
  // Mallorca-Urlaub: ~ 800 € pro Woche all-incl. Familie 3000 €
  const mallorca = annual_cost / 1200;
  if (mallorca >= 0.6) {
    out.push(
      `Deine jährlichen Pendelkosten entsprechen ungefähr ${mallorca >= 1 ? `${mallorca.toFixed(1)} Mallorca-Urlauben` : 'einem Wochenende auf Mallorca'}.`
    );
  }
  if (annual_work_weeks >= 1.5) {
    out.push(`Du verbringst jährlich rund ${annual_work_weeks.toFixed(1)} Arbeitswochen im Auto.`);
  }
  // iPhone equivalent
  const iphones = annual_cost / 1200;
  if (iphones >= 1) {
    out.push(`Das ist der Gegenwert von ca. ${Math.floor(iphones)} neuen iPhones pro Jahr.`);
  }
  if (annual_hours >= 100) {
    out.push(`In dieser Zeit könntest du ${Math.floor(annual_hours / 90)} Spielfilme schauen — oder ${Math.floor(annual_hours / 20)} Bücher lesen.`);
  }
  return out;
}

export function formatEUR(v) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

export function formatNumber(v, digits = 0) {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits }).format(v);
}

export function computeJobSwitch({ currentNet, newNet, currentInputs, newInputs }) {
  const currentCommute = computeResults(currentInputs);
  const newCommute = computeResults(newInputs);

  const net_gain_month = (Number(newNet) || 0) - (Number(currentNet) || 0);
  const extra_commute_month = newCommute.monthly_cost - currentCommute.monthly_cost;
  const real_advantage_month = net_gain_month - extra_commute_month;
  const real_advantage_year = real_advantage_month * 12;

  let verdict;
  if (real_advantage_month >= 200) {
    verdict = `Der Jobwechsel bringt dir voraussichtlich ${formatEUR(real_advantage_month)} mehr pro Monat.`;
  } else if (real_advantage_month >= 50) {
    verdict = `Der Jobwechsel lohnt sich finanziell knapp: ca. ${formatEUR(real_advantage_month)} mehr pro Monat — Lebenszeit und Stress nicht eingerechnet.`;
  } else if (real_advantage_month >= -50) {
    verdict = `Der Gehaltsvorteil wird größtenteils durch den längeren Arbeitsweg aufgezehrt.`;
  } else {
    verdict = `Achtung: Trotz höherem Brutto verlierst du voraussichtlich ${formatEUR(Math.abs(real_advantage_month))} pro Monat durch zusätzliche Pendelkosten.`;
  }

  return {
    net_gain_month,
    extra_commute_month,
    real_advantage_month,
    real_advantage_year,
    verdict,
    currentCommute,
    newCommute,
  };
}
